import { FiltersDefinition, SortingsDefinition } from '@factorialco/f0-react'
import { OneDataCollection, SummariesDefinition } from '@factorialco/f0-react/dist/experimental'

import { DirectReporteesDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { getEmployeeJobRole } from 'modules/job_catalog/lib/utils'

type DirectReporteeRow = {
  id: string
  firstName: string
  lastName: string
  avatarUrl: string | null
  role: string
  forecastedCost: string
  forecastedHeadcount: number
  current: number
}

export const DirectReporteesTable = () => {
  const { employee } = useCurrentSession()

  const source = useGraphqlDataCollectionSource<
    DirectReporteeRow,
    FiltersDefinition,
    SortingsDefinition,
    SummariesDefinition,
    typeof DirectReporteesDataCollectionDocument
  >(
    {
      document: DirectReporteesDataCollectionDocument,
      mapData: (data) => {
        const edges = data.employees.employeesConnection?.edges ?? []

        return edges.map((edge) => {
          const node = edge.node
          return {
            id: node.id,
            firstName: node.firstName,
            lastName: node.lastName,
            avatarUrl: node.avatar?.url ?? null,
            role: getEmployeeJobRole(node.referenceContract),
            forecastedCost: '0€',
            forecastedHeadcount: 0,
            current: node.reportsConnection?.totalCount ?? 0,
          }
        })
      },
      mapVariables: () => ({
        managerIds: [employee.id],
      }),
      mapPagination: (data) => ({
        total: data.employees.employeesConnection?.totalCount ?? 0,
      }),
      paginationType: 'pages',
      search: { enabled: true },
    },
    [employee.id]
  )

  return (
    <OneDataCollection
      fullHeight
      source={source}
      visualizations={[
        {
          type: 'table' as const,
          options: {
            columns: [
              {
                label: i18n.t('planning.direct_reportees_table.columns.name'),
                render: (item) => ({
                  type: 'person' as const,
                  value: {
                    firstName: item.firstName,
                    lastName: item.lastName,
                    src: buildUrl(item.avatarUrl),
                  },
                }),
              },
              {
                label: i18n.t('planning.direct_reportees_table.columns.role'),
                render: (item) => ({
                  type: 'text' as const,
                  value: item.role,
                }),
              },
              {
                label: i18n.t('planning.direct_reportees_table.columns.forecasted_cost'),
                align: 'right' as const,
                render: (item) => ({
                  type: 'text' as const,
                  value: item.forecastedCost,
                }),
              },
              {
                label: i18n.t('planning.direct_reportees_table.columns.forecasted_headcount'),
                align: 'right' as const,
                render: (item) => ({
                  type: 'text' as const,
                  value: String(item.forecastedHeadcount),
                }),
              },
              {
                label: i18n.t('planning.direct_reportees_table.columns.current'),
                align: 'right' as const,
                render: (item) => ({
                  type: 'text' as const,
                  value: String(item.current),
                }),
              },
            ],
          },
        },
      ]}
      emptyStates={{
        'no-data': {
          title: i18n.t('planning.direct_reportees_table.empty_state.title'),
          description: i18n.t('planning.direct_reportees_table.empty_state.description'),
        },
      }}
    />
  )
}

export default DirectReporteesTable
